import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AudioService, { AUDIO_EFFECT } from "../../../../services/AudioService";
import { slidingEmptySlotBackground, SLIDING_BOARD_GUTTER_GRAY } from "./slidingEmptySlotStyle";
import {
    buildHighlightCellMap,
    computeMatchedLineIndices,
    getLineMatchToastPosition,
    lineMatchHighlightOverlaySx,
    lineMatchToastSx,
    LINE_MATCH_EFFECT_MS,
} from "./slidingLineMatchEffects";
import { ghostGuideStepBadgeSx, ghostGuideTileOverlaySx } from "./slidingGhostGuideEffects";

/** Row-major indices `a`, `b` are orthogonal neighbors on a gs×gs grid. */
function gridCellsAdjacent(gs, indexA, indexB) {
    if (indexA < 0 || indexB < 0) return false;
    const ra = Math.floor(indexA / gs);
    const ca = indexA % gs;
    const rb = Math.floor(indexB / gs);
    const cb = indexB % gs;
    return Math.abs(ra - rb) + Math.abs(ca - cb) === 1;
}

/** Drag offset clamped to the axis from tile toward the empty cell. */
function getDragOffset(gs, fromVisual, emptyVisual, deltaX, deltaY, cellSizePx) {
    if (cellSizePx <= 0) return { dx: 0, dy: 0, distance: 0 };
    const rf = Math.floor(fromVisual / gs);
    const cf = fromVisual % gs;
    const re = Math.floor(emptyVisual / gs);
    const ce = emptyVisual % gs;
    let axis = null;
    let sign = 0;
    if (rf === re && ce === cf + 1) {
        axis = "x";
        sign = 1;
    } else if (rf === re && ce === cf - 1) {
        axis = "x";
        sign = -1;
    } else if (cf === ce && re === rf + 1) {
        axis = "y";
        sign = 1;
    } else if (cf === ce && re === rf - 1) {
        axis = "y";
        sign = -1;
    } else {
        return { dx: 0, dy: 0, distance: 0 };
    }

    const raw = axis === "x" ? deltaX : deltaY;
    const signed = sign * raw;
    const clamped = Math.max(0, Math.min(cellSizePx, signed));
    return {
        dx: axis === "x" ? sign * clamped : 0,
        dy: axis === "y" ? sign * clamped : 0,
        distance: clamped,
    };
}

function getCellPixelOrigin(visualCellIdx, gs, boardWidthPx, gapPx) {
    const col = visualCellIdx % gs;
    const row = Math.floor(visualCellIdx / gs);
    const gapTotal = (gs - 1) * gapPx;
    const cellSize = (boardWidthPx - gapTotal) / gs;
    return {
        x: col * (cellSize + gapPx),
        y: row * (cellSize + gapPx),
    };
}

/** Keep tile visually fixed when layout swaps after a partial drag. */
function computeDragCommitTransform(
    fromVisual,
    emptyVisual,
    dragDx,
    dragDy,
    gs,
    boardWidthPx,
    gapPx,
) {
    const from = getCellPixelOrigin(fromVisual, gs, boardWidthPx, gapPx);
    const target = getCellPixelOrigin(emptyVisual, gs, boardWidthPx, gapPx);
    return {
        dx: from.x + dragDx - target.x,
        dy: from.y + dragDy - target.y,
    };
}

function swapTileWithEmpty(prev, visualCellIdx, emptySliceIdx, gs, n) {
    if (prev.length !== n) return null;
    const emptyVisual = prev.findIndex((v) => Number(v) === emptySliceIdx);
    if (emptyVisual < 0 || visualCellIdx === emptyVisual) return null;
    if (!gridCellsAdjacent(gs, visualCellIdx, emptyVisual)) return null;
    const next = [...prev];
    [next[visualCellIdx], next[emptyVisual]] = [next[emptyVisual], next[visualCellIdx]];
    return next;
}

function normalizeLayout(startingLayout, n) {
    if (!Array.isArray(startingLayout) || startingLayout.length !== n || n <= 0) return [];
    return startingLayout.map((x) => Number(x));
}

/** Gutters between tiles — keep in sync with `StoryReferenceWithGrid` GRID_GAP_PX (2px). */
const SLIDING_GRID_GAP_PX = 2;
const SLIDE_TRANSITION_MS = 180;
/** Fraction of cell width/height dragged before the tile commits into the empty slot. */
const DRAG_COMMIT_RATIO = 0.32;
const DRAG_SUPPRESS_CLICK_PX = 6;
/** Solve scatter: tiles fly off, full image fades in, then parent swaps to original image. */
export const SOLVE_REVEAL_MS = 2200;
const SOLVE_REVEAL_IMAGE_FADE_AT = 0.35;
const SOLVE_REVEAL_EASING = "cubic-bezier(0.25, 0.1, 0.25, 1)";

const solvedImageGlow = keyframes`
    0%, 100% { opacity: 0.12; }
    50% { opacity: 0.48; }
`;

const solvedImageSheen = keyframes`
    0% { transform: translateX(-130%) skewX(-18deg); }
    100% { transform: translateX(240%) skewX(-18deg); }
`;

const solvedImageSparkle = keyframes`
    0%, 100% { opacity: 0; transform: scale(0.35); }
    45% { opacity: 1; transform: scale(1); }
`;

const SOLVED_IMAGE_SPARKLES = [
    { top: "12%", left: "14%", delay: "0.2s", size: 7 },
    { top: "22%", right: "16%", delay: "0.7s", size: 9 },
    { bottom: "18%", left: "20%", delay: "1.1s", size: 6 },
    { bottom: "14%", right: "12%", delay: "0.45s", size: 8 },
    { top: "48%", left: "8%", delay: "1.4s", size: 5 },
];

function resolveGapPx(gridGapPx) {
    return gridGapPx != null ? gridGapPx : SLIDING_GRID_GAP_PX;
}

/** Absolute tile position within the board for a row-major visual cell index. */
function getCellPositionSx(visualCellIdx, gs, gridGapPx) {
    const col = visualCellIdx % gs;
    const row = Math.floor(visualCellIdx / gs);
    const gap = resolveGapPx(gridGapPx);

    if (gap <= 0) {
        const sizePct = 100 / gs;
        return {
            left: `${col * sizePct}%`,
            top: `${row * sizePct}%`,
            width: `${sizePct}%`,
            height: `${sizePct}%`,
        };
    }

    const gapTotal = (gs - 1) * gap;
    const cellExpr = `(100% - ${gapTotal}px) / ${gs}`;
    const stepExpr = `((100% - ${gapTotal}px) / ${gs} + ${gap}px)`;
    return {
        width: `calc(${cellExpr})`,
        height: `calc(${cellExpr})`,
        left: `calc(${col} * ${stepExpr})`,
        top: `calc(${row} * ${stepExpr})`,
    };
}

const tileTransformTransitionSx = {
    transition: `transform ${SLIDE_TRANSITION_MS}ms ease-out`,
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
    },
};

const tileSlideTransitionSx = {
    transition: `left ${SLIDE_TRANSITION_MS}ms ease-out, top ${SLIDE_TRANSITION_MS}ms ease-out`,
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
    },
};

/** 1-indexed odd rows → right, even → left; odd cols → up, even → down. */
function getSolveScatterTransform(visualCellIdx, gs) {
    const row = Math.floor(visualCellIdx / gs);
    const col = visualCellIdx % gs;
    const rowNum = row + 1;
    const colNum = col + 1;
    const tx = rowNum % 2 === 1 ? 130 : -130;
    const ty = colNum % 2 === 1 ? -130 : 130;
    return `translate(${tx}%, ${ty}%)`;
}

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Image is divided into gridSize×gridSize equal cells in row-major order (indices 0 … n−1).
 * `layout[visualCell]` = which slice index sits in that board cell (permutation).
 * The hole is the slice with index n−1 (bottom-right in the solved picture).
 */
export default function SlidingPlayMain({
    imageUrl,
    gridSize,
    startingLayout,
    /** Tile index rendered as empty slot; default last slice (n−1) */
    emptyTileIndex = null,
    /** Max width of the board; number → `{ xs: '100%', sm: n }`. Default fills parent width. */
    maxBoardWidth = null,
    /** Called whenever the board permutation changes (for reference overlay sync). */
    onLayoutChange = null,
    /** Called after each valid tile slide (one move). */
    onMove = null,
    /** When solved, message below the board (e.g. translated). Omit for default "Completed". */
    completedLabel,
    /** When true, do not render the solved caption under the board (e.g. parent shows SuccessCard). */
    hideCompletedCaption = false,
    /** When true, board fills a square parent (`width`/`height` 100%) instead of sizing from width alone. */
    fillParent = false,
    /** When false, tile-move sound effects are skipped. */
    playMoveSound = true,
    /** When false, row line-match celebration sound is skipped. */
    playLineMatchSound = true,
    /** Color visible in grid gaps between tiles (e.g. `#fff` on dark mobile play). */
    gridGutterColor = null,
    /** Outer board border color; defaults to theme `divider`. */
    gridBorderColor = null,
    /** Gap between tiles in px; default {@link SLIDING_GRID_GAP_PX}. Use `0` for a seamless board. */
    gridGapPx = null,
    /** Per-tile corner radius (CSS value). */
    tileBorderRadius = "2px",
    /** Inset outline on each tile (static previews — visible on light/dark photo regions). */
    tileOutlineColor = null,
    /** Full CSS `box-shadow` for tiles (overrides {@link tileOutlineColor} when set). */
    tileBoxShadow = null,
    /** Empty hole styling: `default` (theme tint) or `recessed` (dark inset slot). */
    emptySlotVariant = "default",
    /** Animate tile slides into the empty slot (disable for static previews). */
    animateMoves = true,
    /** When true and the board reaches a solved layout, run the scatter reveal before calling {@link onSolvedRevealComplete}. */
    animateSolveReveal = false,
    /** Full image shown after solve (defaults to {@link imageUrl}). */
    revealImageUrl = null,
    /** Show {@link revealImageUrl} immediately with no scatter animation (e.g. already-completed session). */
    startWithRevealImage = false,
    /** Fired once after {@link SOLVE_REVEAL_MS} when {@link animateSolveReveal} scatter finishes. */
    onSolvedRevealComplete = null,
    /** When true, celebrate fully matched rows/columns with highlight + toast (play mode only). */
    lineMatchCelebration = false,
    /** Floating label when a row/column completes; defaults to "Good Job!". */
    lineMatchLabel = "Good Job!",
    /** Map of slice index → step order (1–5) for ghost guide highlight + corner number. */
    ghostGuideMarks = null,
    /** When true, tile taps/drags and board interaction are blocked (ghost guide sequence). */
    boardInputLocked = false,
    /** `"image"` (photo tiles) or `"numbers"` (1…n−1 numbered tiles) — Android PlayScreen tileMode. */
    tileMode = "image",
    /** Dark chrome for number tiles when `tileMode === "numbers"`. */
    numbersDark = false,
}) {
    const showNumbers = tileMode === "numbers";
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : 0;
    const emptyIdx =
        emptyTileIndex != null && Number.isFinite(Number(emptyTileIndex))
            ? Number(emptyTileIndex)
            : n > 0
              ? n - 1
              : -1;

    const resolvedRevealImageUrl =
        typeof revealImageUrl === "string" && revealImageUrl.trim()
            ? revealImageUrl.trim()
            : imageUrl;

    const [layout, setLayout] = useState(() => normalizeLayout(startingLayout, n));
    const [solveRevealActive, setSolveRevealActive] = useState(false);
    const [solveRevealScatter, setSolveRevealScatter] = useState(false);
    const [revealImageVisible, setRevealImageVisible] = useState(false);
    const animatingRef = useRef(false);
    const solveRevealStartedRef = useRef(false);
    const boardRef = useRef(null);
    const dragSessionRef = useRef(null);
    const dragMovedRef = useRef(false);
    const layoutRef = useRef([]);
    const commitSlideTimerRef = useRef(null);
    const [dragVisual, setDragVisual] = useState(null);
    const [commitSlide, setCommitSlide] = useState(null);
    const [dragPointerActive, setDragPointerActive] = useState(false);
    const [lineMatchHighlightMap, setLineMatchHighlightMap] = useState({});
    const [lineMatchToasts, setLineMatchToasts] = useState([]);
    const matchedLinesRef = useRef({ rows: [], cols: [] });
    const lineMatchUserMovedRef = useRef(false);
    const lineMatchToastTimersRef = useRef(new Map());
    const lineMatchHighlightTimerRef = useRef(null);

    layoutRef.current = layout;

    const onLayoutChangeRef = useRef(onLayoutChange);
    const onMoveRef = useRef(onMove);
    const onSolvedRevealCompleteRef = useRef(onSolvedRevealComplete);
    useEffect(() => {
        onLayoutChangeRef.current = onLayoutChange;
        onMoveRef.current = onMove;
        onSolvedRevealCompleteRef.current = onSolvedRevealComplete;
    });

    useEffect(() => {
        onLayoutChangeRef.current?.(layout);
    }, [layout]);

    useEffect(() => {
        const next = normalizeLayout(startingLayout, n);
        setLayout((prev) => {
            if (prev.length === next.length && prev.every((v, i) => Number(v) === Number(next[i]))) {
                return prev;
            }
            return next;
        });
        animatingRef.current = false;
        solveRevealStartedRef.current = false;
        dragSessionRef.current = null;
        dragMovedRef.current = false;
        setDragVisual(null);
        setCommitSlide(null);
        setDragPointerActive(false);
        setLineMatchHighlightMap({});
        setLineMatchToasts([]);
        matchedLinesRef.current = { rows: [], cols: [] };
        lineMatchUserMovedRef.current = false;
        lineMatchToastTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        lineMatchToastTimersRef.current.clear();
        if (lineMatchHighlightTimerRef.current) {
            window.clearTimeout(lineMatchHighlightTimerRef.current);
            lineMatchHighlightTimerRef.current = null;
        }
        if (commitSlideTimerRef.current) {
            window.clearTimeout(commitSlideTimerRef.current);
            commitSlideTimerRef.current = null;
        }
        const solvedLayout =
            next.length === n && next.every((v, i) => Number(v) === i);
        if (startWithRevealImage && solvedLayout) {
            setRevealImageVisible(true);
            setSolveRevealScatter(true);
            setSolveRevealActive(true);
        } else {
            setSolveRevealActive(false);
            setSolveRevealScatter(false);
            setRevealImageVisible(false);
        }
    }, [startingLayout, n, startWithRevealImage]);

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.SLIDING_MOVE);
        if (lineMatchCelebration && playLineMatchSound) {
            AudioService.preload(AUDIO_EFFECT.SLIDING_LINE_MATCH);
        }
        return () => {
            AudioService.release(AUDIO_EFFECT.SLIDING_MOVE);
            if (lineMatchCelebration && playLineMatchSound) {
                AudioService.release(AUDIO_EFFECT.SLIDING_LINE_MATCH);
            }
        };
    }, [lineMatchCelebration, playLineMatchSound]);

    const isSolved = layout.length === n && layout.every((v, i) => Number(v) === i);

    const scheduleLineMatchToastRemoval = useCallback((toastId) => {
        const timers = lineMatchToastTimersRef.current;
        const existing = timers.get(toastId);
        if (existing) {
            window.clearTimeout(existing);
        }
        const timerId = window.setTimeout(() => {
            timers.delete(toastId);
            setLineMatchToasts((prev) => prev.filter((toast) => toast.id !== toastId));
        }, LINE_MATCH_EFFECT_MS);
        timers.set(toastId, timerId);
    }, []);

    const scheduleLineMatchHighlightClear = useCallback(() => {
        if (lineMatchHighlightTimerRef.current) {
            window.clearTimeout(lineMatchHighlightTimerRef.current);
        }
        lineMatchHighlightTimerRef.current = window.setTimeout(() => {
            lineMatchHighlightTimerRef.current = null;
            setLineMatchHighlightMap({});
        }, LINE_MATCH_EFFECT_MS);
    }, []);

    useEffect(() => {
        return () => {
            lineMatchToastTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
            lineMatchToastTimersRef.current.clear();
            if (lineMatchHighlightTimerRef.current) {
                window.clearTimeout(lineMatchHighlightTimerRef.current);
                lineMatchHighlightTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!lineMatchCelebration || solveRevealActive || isSolved || layout.length !== n) {
            setLineMatchHighlightMap({});
            setLineMatchToasts((prev) => {
                if (!prev.length) return prev;
                prev.forEach((toast) => {
                    const timerId = lineMatchToastTimersRef.current.get(toast.id);
                    if (timerId) {
                        window.clearTimeout(timerId);
                        lineMatchToastTimersRef.current.delete(toast.id);
                    }
                });
                return [];
            });
            matchedLinesRef.current = { rows: [], cols: [] };
            return;
        }

        const { rows, cols } = computeMatchedLineIndices(layout, gs);

        // Easy layouts may already have correct rows/columns — record them as baseline only,
        // no celebration until the player makes at least one move.
        if (!lineMatchUserMovedRef.current) {
            matchedLinesRef.current = { rows, cols };
            setLineMatchHighlightMap({});
            return;
        }

        const prev = matchedLinesRef.current;
        const newRows = rows.filter((rowIndex) => !prev.rows.includes(rowIndex));
        const newCols = cols.filter((colIndex) => !prev.cols.includes(colIndex));
        matchedLinesRef.current = { rows, cols };

        if (newRows.length || newCols.length) {
            if (newRows.length && playLineMatchSound) {
                AudioService.play(AUDIO_EFFECT.SLIDING_LINE_MATCH);
            }
            const effectStamp = Date.now();
            setLineMatchHighlightMap(buildHighlightCellMap(newRows, newCols, gs, effectStamp));
            scheduleLineMatchHighlightClear();
        } else {
            if (lineMatchHighlightTimerRef.current) {
                window.clearTimeout(lineMatchHighlightTimerRef.current);
                lineMatchHighlightTimerRef.current = null;
            }
            setLineMatchHighlightMap({});
        }

        setLineMatchToasts((prevToasts) => {
            const nextToasts = prevToasts.filter(
                (toast) =>
                    (toast.kind === "row" && rows.includes(toast.index)) ||
                    (toast.kind === "col" && cols.includes(toast.index)),
            );
            prevToasts.forEach((toast) => {
                if (nextToasts.some((entry) => entry.id === toast.id)) {
                    return;
                }
                const timerId = lineMatchToastTimersRef.current.get(toast.id);
                if (timerId) {
                    window.clearTimeout(timerId);
                    lineMatchToastTimersRef.current.delete(toast.id);
                }
            });

            if (!newRows.length && !newCols.length) {
                return nextToasts.length === prevToasts.length ? prevToasts : nextToasts;
            }

            const createdAt = Date.now();
            const added = [
                ...newRows.map((rowIndex) => ({
                    id: `row-${rowIndex}-${createdAt}-${Math.random().toString(36).slice(2, 7)}`,
                    kind: "row",
                    index: rowIndex,
                })),
                ...newCols.map((colIndex) => ({
                    id: `col-${colIndex}-${createdAt}-${Math.random().toString(36).slice(2, 7)}`,
                    kind: "col",
                    index: colIndex,
                })),
            ];
            added.forEach((toast) => scheduleLineMatchToastRemoval(toast.id));
            return [...nextToasts, ...added];
        });
    }, [
        gs,
        isSolved,
        layout,
        lineMatchCelebration,
        n,
        playLineMatchSound,
        scheduleLineMatchToastRemoval,
        scheduleLineMatchHighlightClear,
        solveRevealActive,
    ]);

    useEffect(() => {
        if (!isSolved || !animateSolveReveal || solveRevealStartedRef.current) {
            return undefined;
        }
        solveRevealStartedRef.current = true;
        const revealMs = prefersReducedMotion() ? 0 : SOLVE_REVEAL_MS;
        animatingRef.current = true;
        setSolveRevealActive(true);

        const scatterRaf = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => setSolveRevealScatter(true));
        });

        const imageTimer = window.setTimeout(
            () => setRevealImageVisible(true),
            revealMs * SOLVE_REVEAL_IMAGE_FADE_AT,
        );
        const completeTimer = window.setTimeout(() => {
            animatingRef.current = false;
            onSolvedRevealCompleteRef.current?.();
        }, revealMs);

        return () => {
            window.cancelAnimationFrame(scatterRaf);
            window.clearTimeout(imageTimer);
            window.clearTimeout(completeTimer);
        };
    }, [isSolved, animateSolveReveal]);

    const applyLayoutSwap = useCallback(
        (visualCellIdx) => {
            let swapped = false;
            setLayout((prev) => {
                const next = swapTileWithEmpty(prev, visualCellIdx, emptyIdx, gs, n);
                if (!next) return prev;
                swapped = true;
                if (lineMatchCelebration) {
                    lineMatchUserMovedRef.current = true;
                }
                queueMicrotask(() => {
                    onLayoutChangeRef.current?.(next);
                    onMoveRef.current?.();
                });
                if (playMoveSound) {
                    AudioService.play(AUDIO_EFFECT.SLIDING_MOVE);
                }
                return next;
            });
            return swapped;
        },
        [emptyIdx, gs, n, playMoveSound, lineMatchCelebration],
    );

    const handleTileActivate = useCallback(
        (visualCellIdx) => {
            if (boardInputLocked || animatingRef.current || solveRevealActive || commitSlide) return;
            if (animateMoves) {
                animatingRef.current = true;
                window.setTimeout(() => {
                    animatingRef.current = false;
                }, SLIDE_TRANSITION_MS);
            }
            applyLayoutSwap(visualCellIdx);
        },
        [animateMoves, applyLayoutSwap, boardInputLocked, solveRevealActive, commitSlide],
    );

    const getCellSizePx = useCallback(() => {
        const el = boardRef.current;
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        const gap = resolveGapPx(gridGapPx);
        const gapTotal = (gs - 1) * gap;
        return Math.max(0, (rect.width - gapTotal) / gs);
    }, [gs, gridGapPx]);

    const getBoardWidthPx = useCallback(() => {
        const el = boardRef.current;
        if (!el) return 0;
        return el.getBoundingClientRect().width;
    }, []);

    const finishDrag = useCallback(
        (pointerId) => {
            const session = dragSessionRef.current;
            if (!session || session.pointerId !== pointerId) return;

            const cellSizePx = getCellSizePx();
            const distance = Math.max(Math.abs(session.dx), Math.abs(session.dy));
            const { visualCellIdx, emptyVisualPos, dx, dy } = session;
            const sliceIndex = Number(layoutRef.current[visualCellIdx]);
            const boardWidthPx = getBoardWidthPx();
            const gapPx = resolveGapPx(gridGapPx);

            dragSessionRef.current = null;
            setDragPointerActive(false);

            if (cellSizePx > 0 && distance >= cellSizePx * DRAG_COMMIT_RATIO) {
                setDragVisual(null);
                const compensation = computeDragCommitTransform(
                    visualCellIdx,
                    emptyVisualPos,
                    dx,
                    dy,
                    gs,
                    boardWidthPx,
                    gapPx,
                );

                if (animateMoves) {
                    animatingRef.current = true;
                    setCommitSlide({
                        sliceIndex,
                        dx: compensation.dx,
                        dy: compensation.dy,
                        phase: "hold",
                    });
                }

                applyLayoutSwap(visualCellIdx);

                if (animateMoves) {
                    window.requestAnimationFrame(() => {
                        window.requestAnimationFrame(() => {
                            setCommitSlide((prev) =>
                                prev?.sliceIndex === sliceIndex
                                    ? { ...prev, dx: 0, dy: 0, phase: "slide" }
                                    : prev,
                            );
                        });
                    });
                    if (commitSlideTimerRef.current) {
                        window.clearTimeout(commitSlideTimerRef.current);
                    }
                    commitSlideTimerRef.current = window.setTimeout(() => {
                        commitSlideTimerRef.current = null;
                        setCommitSlide(null);
                        animatingRef.current = false;
                    }, SLIDE_TRANSITION_MS);
                } else {
                    setCommitSlide(null);
                }
                return;
            }

            setDragVisual({ visualCellIdx, dx, dy, releasing: false });
            window.requestAnimationFrame(() => {
                setDragVisual({ visualCellIdx, dx: 0, dy: 0, releasing: true });
            });
            if (commitSlideTimerRef.current) {
                window.clearTimeout(commitSlideTimerRef.current);
            }
            commitSlideTimerRef.current = window.setTimeout(() => {
                commitSlideTimerRef.current = null;
                setDragVisual(null);
            }, SLIDE_TRANSITION_MS);
        },
        [animateMoves, applyLayoutSwap, getBoardWidthPx, getCellSizePx, gridGapPx, gs],
    );

    const updateDragFromPointer = useCallback(
        (event) => {
            const session = dragSessionRef.current;
            if (!session || session.pointerId !== event.pointerId) return;

            const deltaX = event.clientX - session.startX;
            const deltaY = event.clientY - session.startY;
            if (
                Math.abs(deltaX) > DRAG_SUPPRESS_CLICK_PX ||
                Math.abs(deltaY) > DRAG_SUPPRESS_CLICK_PX
            ) {
                dragMovedRef.current = true;
                event.preventDefault();
            }

            const cellSizePx = getCellSizePx();
            const { dx, dy } = getDragOffset(
                gs,
                session.visualCellIdx,
                session.emptyVisualPos,
                deltaX,
                deltaY,
                cellSizePx,
            );
            session.dx = dx;
            session.dy = dy;
            setDragVisual({ visualCellIdx: session.visualCellIdx, dx, dy, releasing: false });
        },
        [getCellSizePx, gs],
    );

    useEffect(() => {
        if (!dragPointerActive) return undefined;

        const onMove = (event) => updateDragFromPointer(event);
        const onEnd = (event) => finishDrag(event.pointerId);

        window.addEventListener("pointermove", onMove, { passive: false });
        window.addEventListener("pointerup", onEnd);
        window.addEventListener("pointercancel", onEnd);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onEnd);
            window.removeEventListener("pointercancel", onEnd);
        };
    }, [dragPointerActive, finishDrag, updateDragFromPointer]);

    const handleTilePointerDown = useCallback(
        (visualCellIdx, emptyVisualPos, switchable, event) => {
            if (
                boardInputLocked ||
                !switchable ||
                animatingRef.current ||
                solveRevealActive ||
                commitSlide
            ) {
                return;
            }
            event.preventDefault();
            dragMovedRef.current = false;
            dragSessionRef.current = {
                visualCellIdx,
                emptyVisualPos,
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                dx: 0,
                dy: 0,
            };
            setDragVisual({ visualCellIdx, dx: 0, dy: 0, releasing: false });
            setDragPointerActive(true);
        },
        [boardInputLocked, commitSlide, solveRevealActive],
    );

    const handleTileClick = useCallback(
        (visualCellIdx) => {
            if (dragMovedRef.current) {
                dragMovedRef.current = false;
                return;
            }
            handleTileActivate(visualCellIdx);
        },
        [handleTileActivate],
    );

    if (!imageUrl || typeof imageUrl !== "string" || !imageUrl.trim()) {
        return (
            <Typography variant="caption" color="text.secondary">
                No image URL
            </Typography>
        );
    }

    if (!Number.isFinite(gs) || gs < 1 || !Array.isArray(startingLayout) || startingLayout.length !== n) {
        return (
            <Typography variant="caption" color="text.secondary">
                Invalid grid or layout (expect length {n || "gridSize²"})
            </Typography>
        );
    }

    if (layout.length !== n) {
        return (
            <Typography variant="caption" color="text.secondary">
                Invalid grid or layout (expect length {n || "gridSize²"})
            </Typography>
        );
    }

    const emptyVisualPos = layout.findIndex((v) => Number(v) === emptyIdx);

    const effectiveGapPx = solveRevealActive ? 0 : gridGapPx;
    const solveRevealTransitionSx = {
        transition: [
            `left ${SOLVE_REVEAL_MS}ms ${SOLVE_REVEAL_EASING}`,
            `top ${SOLVE_REVEAL_MS}ms ${SOLVE_REVEAL_EASING}`,
            `transform ${SOLVE_REVEAL_MS}ms ${SOLVE_REVEAL_EASING}`,
            `opacity ${Math.round(SOLVE_REVEAL_MS * 0.55)}ms ${SOLVE_REVEAL_EASING} ${Math.round(SOLVE_REVEAL_MS * 0.4)}ms`,
        ].join(", "),
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    };

    const resolvedBoardBg = showNumbers
        ? numbersDark
            ? "#0f172a"
            : "#e8edf2"
        : gridGutterColor ??
          (emptySlotVariant === "recessed" ? "#1a1d21" : SLIDING_BOARD_GUTTER_GRAY);

    const resolvedMaxBoard =
        maxBoardWidth == null
            ? "100%"
            : typeof maxBoardWidth === "number"
              ? { xs: "100%", sm: maxBoardWidth }
              : maxBoardWidth;

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: resolvedMaxBoard,
                ...(fillParent ? { height: "100%", minHeight: 0 } : {}),
            }}
        >
            <Box
                ref={boardRef}
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    ...(fillParent ? { height: "100%", width: "100%", minHeight: 0, minWidth: 0 } : {}),
                    /* Keep border width stable so the image frame doesn't jump when revealing. */
                    border: 1,
                    borderStyle: "solid",
                    borderColor: solveRevealActive
                        ? "transparent"
                        : (gridBorderColor ?? "divider"),
                    borderRadius: 1,
                    overflow: "visible",
                    bgcolor: resolvedBoardBg,
                    touchAction: solveRevealActive ? "auto" : "none",
                    boxSizing: "border-box",
                    /* Number tiles size type from the board (cqw) so labels stay large on 1080p. */
                    ...(showNumbers ? { containerType: "size" } : null),
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        overflow: "hidden",
                        borderRadius: "inherit",
                    }}
                >
                {revealImageVisible ? (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 0,
                            overflow: "hidden",
                            pointerEvents: "none",
                            opacity: revealImageVisible ? 1 : 0,
                            transition: `opacity ${SOLVE_REVEAL_MS * (1 - SOLVE_REVEAL_IMAGE_FADE_AT)}ms ${SOLVE_REVEAL_EASING}`,
                            "@media (prefers-reduced-motion: reduce)": {
                                transition: "none",
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={resolvedRevealImageUrl}
                            alt=""
                            draggable={false}
                            referrerPolicy="no-referrer"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                /* Match tile image slicing (`objectFit: fill`) so the reveal
                                   lands exactly where the reconstructed grid sat. */
                                objectFit: "fill",
                                objectPosition: "center",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background:
                                    "radial-gradient(circle at 50% 40%, rgba(253, 224, 71, 0.55), rgba(253, 224, 71, 0) 68%)",
                                animation: `${solvedImageGlow} 1.6s ease-in-out 2`,
                                "@media (prefers-reduced-motion: reduce)": {
                                    animation: "none",
                                    opacity: 0,
                                },
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                top: "-20%",
                                bottom: "-20%",
                                width: "38%",
                                background:
                                    "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.72), rgba(255,255,255,0))",
                                animation: `${solvedImageSheen} 1.5s ease-in-out 2`,
                                "@media (prefers-reduced-motion: reduce)": {
                                    animation: "none",
                                    opacity: 0,
                                },
                            }}
                        />
                        {SOLVED_IMAGE_SPARKLES.map((sparkle) => (
                            <Box
                                key={`${sparkle.top || ""}-${sparkle.left || sparkle.right || ""}`}
                                sx={{
                                    position: "absolute",
                                    top: sparkle.top,
                                    left: sparkle.left,
                                    right: sparkle.right,
                                    bottom: sparkle.bottom,
                                    width: sparkle.size,
                                    height: sparkle.size,
                                    borderRadius: "50%",
                                    bgcolor: "#fff8dc",
                                    boxShadow: "0 0 8px rgba(253, 224, 71, 0.95)",
                                    animation: `${solvedImageSparkle} 1.4s ease-in-out 2`,
                                    animationDelay: sparkle.delay,
                                    "@media (prefers-reduced-motion: reduce)": {
                                        animation: "none",
                                        opacity: 0,
                                    },
                                }}
                            />
                        ))}
                    </Box>
                ) : null}
                {!isSolved && emptyVisualPos >= 0 ? (
                    <Box
                        key="empty-slot"
                        sx={{
                            position: "absolute",
                            boxSizing: "border-box",
                            ...getCellPositionSx(emptyVisualPos, gs, effectiveGapPx),
                            ...(animateMoves ? tileSlideTransitionSx : {}),
                            overflow: "hidden",
                            borderRadius: tileBorderRadius,
                            pointerEvents: "none",
                            zIndex: 0,
                            ...(tileBoxShadow
                                ? { boxShadow: tileBoxShadow }
                                : tileOutlineColor
                                  ? { boxShadow: `inset 0 0 0 1px ${tileOutlineColor}` }
                                  : {}),
                        }}
                    >
                        {showNumbers ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    boxSizing: "border-box",
                                    p: "2px",
                                    bgcolor: numbersDark ? "#060A12" : "#E2E8F0",
                                    borderRadius: tileBorderRadius,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "4px",
                                        bgcolor: numbersDark ? "#060A12" : "#E2E8F0",
                                        borderTop: "2px solid",
                                        borderLeft: "2px solid",
                                        borderColor: numbersDark
                                            ? "rgba(0, 0, 0, 0.55)"
                                            : "rgba(15, 23, 42, 0.12)",
                                        borderBottom: "1px solid",
                                        borderRight: "1px solid",
                                        borderBottomColor: numbersDark
                                            ? "rgba(148, 163, 184, 0.12)"
                                            : "rgba(255, 255, 255, 0.7)",
                                        borderRightColor: numbersDark
                                            ? "rgba(148, 163, 184, 0.12)"
                                            : "rgba(255, 255, 255, 0.7)",
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                    overflow: "hidden",
                                    bgcolor:
                                        emptySlotVariant === "recessed"
                                            ? "transparent"
                                            : emptySlotVariant === "marble"
                                              ? "#F5F2EB"
                                              : "#ffffff",
                                    "& img": {
                                        opacity:
                                            emptySlotVariant === "recessed"
                                                ? 0.14
                                                : emptySlotVariant === "marble"
                                                  ? 0
                                                  : 1,
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    draggable={false}
                                    referrerPolicy="no-referrer"
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        width: `${gs * 100}%`,
                                        height: `${gs * 100}%`,
                                        maxWidth: "none",
                                        objectFit: "fill",
                                        transform: `translate(-${((emptyIdx % gs) * 100) / gs}%, -${(Math.floor(emptyIdx / gs) * 100) / gs}%)`,
                                        transformOrigin: "top left",
                                        userSelect: "none",
                                        pointerEvents: "none",
                                    }}
                                />
                                <Box
                                    sx={(theme) =>
                                        emptySlotVariant === "recessed"
                                            ? {
                                                  position: "absolute",
                                                  inset: 0,
                                                  bgcolor: "#1a1d21",
                                                  boxShadow: "inset 0 3px 10px rgba(0, 0, 0, 0.45)",
                                                  pointerEvents: "none",
                                              }
                                            : emptySlotVariant === "marble"
                                              ? {
                                                    position: "absolute",
                                                    inset: 0,
                                                    background:
                                                        "linear-gradient(145deg, #FFFdf8 0%, #F5F2EB 42%, #EDE8DF 100%)",
                                                    boxShadow:
                                                        "inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 2px rgba(44, 58, 74, 0.06)",
                                                    pointerEvents: "none",
                                                }
                                              : {
                                                    position: "absolute",
                                                    inset: 0,
                                                    backgroundColor: slidingEmptySlotBackground(theme),
                                                    pointerEvents: "none",
                                                }
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                ) : null}
                {Array.from({ length: n }, (_, sliceIndex) => {
                    const visualCellIdx = layout.findIndex((v) => Number(v) === sliceIndex);
                    if (visualCellIdx < 0) return null;

                    if (sliceIndex === emptyIdx && !isSolved) return null;

                    const col = sliceIndex % gs;
                    const row = Math.floor(sliceIndex / gs);
                    const cellPositionSx = getCellPositionSx(visualCellIdx, gs, effectiveGapPx);

                    const switchable =
                        !boardInputLocked &&
                        !solveRevealActive &&
                        emptyVisualPos >= 0 &&
                        visualCellIdx !== emptyVisualPos &&
                        gridCellsAdjacent(gs, visualCellIdx, emptyVisualPos);

                    const scatterTransform =
                        solveRevealScatter && solveRevealActive
                            ? getSolveScatterTransform(visualCellIdx, gs)
                            : undefined;

                    const isDraggingTile =
                        dragVisual?.visualCellIdx === visualCellIdx && !solveRevealActive;
                    const isCommittingTile =
                        commitSlide?.sliceIndex === sliceIndex && !solveRevealActive;

                    let tileTransform = scatterTransform;
                    let useTransformTransition = false;

                    if (isCommittingTile) {
                        tileTransform = `translate(${commitSlide.dx}px, ${commitSlide.dy}px)`;
                        useTransformTransition = commitSlide.phase === "slide";
                    } else if (isDraggingTile && (dragVisual.dx || dragVisual.dy)) {
                        tileTransform = `translate(${dragVisual.dx}px, ${dragVisual.dy}px)`;
                        useTransformTransition = dragVisual.releasing === true;
                    }

                    const usePositionTransition =
                        animateMoves &&
                        !isDraggingTile &&
                        !isCommittingTile &&
                        !solveRevealActive;

                    return (
                        <Box
                            key={`tile-${sliceIndex}`}
                            onClick={
                                switchable ? () => handleTileClick(visualCellIdx) : undefined
                            }
                            onPointerDown={
                                switchable
                                    ? (event) =>
                                          handleTilePointerDown(
                                              visualCellIdx,
                                              emptyVisualPos,
                                              switchable,
                                              event,
                                          )
                                    : undefined
                            }
                            role={switchable ? "button" : undefined}
                            tabIndex={switchable ? 0 : undefined}
                            onKeyDown={
                                switchable
                                    ? (e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              handleTileActivate(visualCellIdx);
                                          }
                                      }
                                    : undefined
                            }
                            sx={{
                                position: "absolute",
                                boxSizing: "border-box",
                                ...cellPositionSx,
                                ...(solveRevealActive
                                    ? solveRevealTransitionSx
                                    : usePositionTransition
                                      ? tileSlideTransitionSx
                                      : {}),
                                ...(useTransformTransition ? tileTransformTransitionSx : {}),
                                overflow: "hidden",
                                borderRadius: solveRevealActive ? 0 : tileBorderRadius,
                                cursor: switchable ? "grab" : "default",
                                outline: "none",
                                ...(tileBoxShadow
                                    ? { boxShadow: tileBoxShadow }
                                    : tileOutlineColor
                                      ? { boxShadow: `inset 0 0 0 1px ${tileOutlineColor}` }
                                      : {}),
                                zIndex: isDraggingTile || isCommittingTile ? 2 : 1,
                                transform: tileTransform,
                                opacity: solveRevealScatter ? 0 : 1,
                                ...(isDraggingTile ? { cursor: "grabbing" } : {}),
                            }}
                        >
                            {showNumbers && !solveRevealActive ? (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: "3px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "14px",
                                        bgcolor: numbersDark ? "#334155" : "#ffffff",
                                        backgroundImage: numbersDark
                                            ? "linear-gradient(165deg, #45566e 0%, #314054 52%, #243041 100%)"
                                            : "linear-gradient(165deg, #ffffff 0%, #f7fafc 45%, #e8eef5 100%)",
                                        boxShadow: numbersDark
                                            ? "0 6px 14px rgba(2, 6, 23, 0.55), inset 0 1px 0 rgba(255,255,255,0.1)"
                                            : "0 6px 16px rgba(15, 23, 42, 0.14), inset 0 1px 0 #fff",
                                        userSelect: "none",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            color: numbersDark ? "#F8FAFC" : "#0B1220",
                                            fontFamily:
                                                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                                            fontWeight: 800,
                                            /*
                                             * ~52% of a tile: board cqw / grid. On a ~1000px 4×4 board ≈ 130px.
                                             * vmin fallback when container queries are unavailable.
                                             */
                                            fontSize: `clamp(2.5rem, ${52 / gs}cqw, 8rem)`,
                                            "@supports not (width: 1cqw)": {
                                                fontSize: `clamp(2.5rem, ${13 / gs}vmin, 8rem)`,
                                            },
                                            fontVariantNumeric: "tabular-nums",
                                            letterSpacing: "-0.05em",
                                            lineHeight: 1,
                                            textShadow: numbersDark
                                                ? "0 3px 12px rgba(0, 0, 0, 0.4)"
                                                : "0 2px 0 rgba(255, 255, 255, 0.9), 0 1px 2px rgba(15, 23, 42, 0.08)",
                                        }}
                                    >
                                        {sliceIndex + 1}
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    draggable={false}
                                    referrerPolicy="no-referrer"
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        width: `${gs * 100}%`,
                                        height: `${gs * 100}%`,
                                        maxWidth: "none",
                                        objectFit: "fill",
                                        transform: `translate(-${(col * 100) / gs}%, -${(row * 100) / gs}%)`,
                                        transformOrigin: "top left",
                                        userSelect: "none",
                                        pointerEvents: "none",
                                    }}
                                />
                            )}
                            {lineMatchCelebration && lineMatchHighlightMap[visualCellIdx] ? (
                                <Box
                                    key={lineMatchHighlightMap[visualCellIdx]}
                                    aria-hidden
                                    sx={lineMatchHighlightOverlaySx(
                                        lineMatchHighlightMap[visualCellIdx],
                                    )}
                                />
                            ) : null}
                            {ghostGuideMarks &&
                            ghostGuideMarks[String(sliceIndex)] != null ? (
                                <>
                                    <Box aria-hidden sx={ghostGuideTileOverlaySx()} />
                                    <Box
                                        aria-hidden
                                        sx={ghostGuideStepBadgeSx(
                                            String(ghostGuideMarks[String(sliceIndex)]),
                                        )}
                                    >
                                        {ghostGuideMarks[String(sliceIndex)]}
                                    </Box>
                                </>
                            ) : null}
                        </Box>
                    );
                })}
                </Box>
                {lineMatchCelebration
                    ? lineMatchToasts.map((toast) => (
                          <Box
                              key={toast.id}
                              component="span"
                              aria-live="polite"
                              sx={lineMatchToastSx(
                                  getLineMatchToastPosition(toast.kind, toast.index, gs),
                              )}
                          >
                              {lineMatchLabel}
                          </Box>
                      ))
                    : null}
            </Box>
            {isSolved && !hideCompletedCaption ? (
                <Typography
                    variant="subtitle2"
                    color="success.main"
                    sx={{ mt: 1, textAlign: "center", fontWeight: 600 }}
                >
                    {completedLabel ?? "Completed"}
                </Typography>
            ) : null}
        </Box>
    );
}

SlidingPlayMain.propTypes = {
    imageUrl: PropTypes.string,
    gridSize: PropTypes.number,
    startingLayout: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
    emptyTileIndex: PropTypes.number,
    maxBoardWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    onLayoutChange: PropTypes.func,
    onMove: PropTypes.func,
    completedLabel: PropTypes.string,
    hideCompletedCaption: PropTypes.bool,
    fillParent: PropTypes.bool,
    playMoveSound: PropTypes.bool,
    playLineMatchSound: PropTypes.bool,
    gridGutterColor: PropTypes.string,
    gridBorderColor: PropTypes.string,
    gridGapPx: PropTypes.number,
    tileBorderRadius: PropTypes.string,
    tileOutlineColor: PropTypes.string,
    tileBoxShadow: PropTypes.string,
    emptySlotVariant: PropTypes.oneOf(["default", "recessed", "marble"]),
    animateMoves: PropTypes.bool,
    animateSolveReveal: PropTypes.bool,
    revealImageUrl: PropTypes.string,
    startWithRevealImage: PropTypes.bool,
    onSolvedRevealComplete: PropTypes.func,
    lineMatchCelebration: PropTypes.bool,
    lineMatchLabel: PropTypes.string,
    ghostGuideMarks: PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ),
    boardInputLocked: PropTypes.bool,
    tileMode: PropTypes.oneOf(["image", "numbers"]),
    numbersDark: PropTypes.bool,
};
